import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@toki/auth';
import type { Festivo, FestivoAmbito } from '../services/api';
import { createFestivo, deleteFestivo, listFestivos } from '../services/api';
import styles from './Mantenimiento.module.css';

/**
 * FESTIVOS del reloj del SLA (paso 89b, K64): días que no cuentan para el
 * plazo de primera respuesta, en TODOS los packs. Es el calendario laboral de
 * Adaptia, no el de cada cliente.
 *
 * Solo admin (`sla:manage`), como los packs. Añadir o quitar un festivo NO
 * mueve los plazos ya calculados (D2 de Jose): vale para los tickets que
 * entren a partir de ahora. Se dice en el formulario, igual que en los packs.
 *
 * El aviso de «faltan los del año que viene» sale desde el 1 de octubre: el
 * BON de Navarra publica el calendario del año siguiente entre mayo y junio,
 * y el BOE en octubre, así que para entonces ya se pueden cargar.
 */

const AMBITOS: [FestivoAmbito, string][] = [
  ['nacional', 'Nacional'], ['autonomico', 'Navarra'], ['local', 'Local'],
];
const ambitoLabel = (a: FestivoAmbito) => AMBITOS.find(([k]) => k === a)?.[1] ?? a;

const DIA_SEMANA = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

/** «AAAA-MM-DD» → Date a mediodía local: sin hora, un `new Date('2026-10-12')`
 *  es medianoche UTC y en América sería el día 11. */
function dia(fecha: string): Date {
  const [y, m, d] = fecha.split('-').map(Number);
  return new Date(y!, m! - 1, d!, 12);
}

function fechaLarga(fecha: string): string {
  const d = dia(fecha);
  return `${DIA_SEMANA[d.getDay()]} ${d.getDate()}/${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const finDeSemana = (fecha: string) => [0, 6].includes(dia(fecha).getDay());

export function FestivosPanel() {
  const { provider } = useAuth();
  const hoy = new Date();
  const [festivos, setFestivos] = useState<Festivo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [year, setYear] = useState(hoy.getFullYear());
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ fecha: '', nombre: '', ambito: 'local' as FestivoAmbito });
  const [saving, setSaving] = useState(false);
  const [quitar, setQuitar] = useState<Festivo | null>(null);

  const load = useCallback(async () => {
    const token = await provider.getAccessToken();
    if (!token) return;
    try {
      setFestivos(await listFestivos(token));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [provider]);

  useEffect(() => { load(); }, [load]);

  const delAnio = festivos.filter((f) => f.fecha.startsWith(`${year}-`));
  const siguiente = hoy.getFullYear() + 1;
  const faltaSiguiente = hoy.getMonth() >= 9 && !festivos.some((f) => f.fecha.startsWith(`${siguiente}-`));

  const save = async () => {
    const token = await provider.getAccessToken();
    if (!token || !form.fecha || !form.nombre.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await createFestivo({ ...form, nombre: form.nombre.trim() }, token);
      setOpen(false);
      setYear(Number(form.fecha.slice(0, 4)));
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const confirmQuitar = async () => {
    const token = await provider.getAccessToken();
    if (!token || !quitar) return;
    setSaving(true);
    setError(null);
    try {
      await deleteFestivo(quitar.id, token);
      setQuitar(null);
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className={styles.loading}>Cargando…</div>;

  return (
    <>
      <p className={styles.muted} style={{ margin: '0 0 14px' }}>
        Días que no cuentan para el plazo de primera respuesta, en todos los SLA. Por ejemplo, con el
        lunes 12 de octubre festivo, un ticket crítico del viernes a las 17:30 vence el martes a las 8:30.
      </p>
      {faltaSiguiente && (
        <div className={styles.notice} style={{ marginBottom: 12 }}>
          Aún no hay festivos de {siguiente}. Añádelos antes de enero: sin ellos, el reloj cuenta los
          festivos de {siguiente} como días de trabajo.
        </div>
      )}
      {error && !open && !quitar && <div className={styles.error} style={{ margin: '0 0 12px' }}>{error}</div>}

      <div className={styles.toolbar}>
        <button className={styles.cancelBtn} onClick={() => setYear((y) => y - 1)} aria-label="Año anterior">‹</button>
        <strong className={styles.num}>{year}</strong>
        <button className={styles.cancelBtn} onClick={() => setYear((y) => y + 1)} aria-label="Año siguiente">›</button>
        <span className={styles.count}>{delAnio.length} festivos</span>
        <button className={styles.createBtn}
          onClick={() => { setForm({ fecha: '', nombre: '', ambito: 'local' }); setError(null); setOpen(true); }}>
          + Añadir festivo
        </button>
      </div>

      <div className={styles.tableCard}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Día</th>
              <th>Festivo</th>
              <th>Ámbito</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {delAnio.map((f) => (
              <tr key={f.id}>
                <td className={styles.num}>
                  {fechaLarga(f.fecha)}
                  {finDeSemana(f.fecha) && <span className={styles.sub}>Fin de semana: no cambia nada</span>}
                </td>
                <td>{f.nombre}</td>
                <td>{ambitoLabel(f.ambito)}</td>
                <td>
                  <div className={styles.actions}>
                    <button className={styles.deactivateBtn} onClick={() => { setError(null); setQuitar(f); }}>Quitar</button>
                  </div>
                </td>
              </tr>
            ))}
            {!delAnio.length && (
              <tr><td colSpan={4} className={styles.muted}>No hay festivos de {year}.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Añadir ───────────────────────────────────────── */}
      {open && (
        <div className={styles.backdrop} onClick={() => setOpen(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Añadir festivo">
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Añadir festivo</h3>
              <button type="button" className={styles.modalClose} onClick={() => setOpen(false)} aria-label="Cerrar">&times;</button>
            </div>
            {error && <div className={styles.error}>{error}</div>}
            <div className={styles.modalBody}>
              <div className={styles.fieldRow}>
                <div className={styles.fieldGroup}>
                  <label className={styles.label} htmlFor="fest-fecha">Día</label>
                  <input id="fest-fecha" className={styles.input} type="date" value={form.fecha}
                    onChange={(e) => setForm((f) => ({ ...f, fecha: e.target.value }))} />
                </div>
                <div className={styles.fieldGroup}>
                  <label className={styles.label} htmlFor="fest-ambito">Ámbito</label>
                  <select id="fest-ambito" className={styles.select} value={form.ambito}
                    onChange={(e) => setForm((f) => ({ ...f, ambito: e.target.value as FestivoAmbito }))}>
                    {AMBITOS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                  </select>
                </div>
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.label} htmlFor="fest-nombre">Nombre</label>
                <input id="fest-nombre" className={styles.input} value={form.nombre} placeholder="San Fermín"
                  onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))} />
              </div>
              <span className={styles.muted}>
                Vale para los tickets que entren a partir de ahora: los abiertos conservan su plazo.
              </span>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => setOpen(false)}>Cancelar</button>
              <button className={styles.saveBtn} onClick={save} disabled={saving || !form.fecha || !form.nombre.trim()}>
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Quitar ───────────────────────────────────────── */}
      {quitar && (
        <div className={styles.backdrop} onClick={() => setQuitar(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Quitar festivo">
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Quitar {quitar.nombre}</h3>
              <button type="button" className={styles.modalClose} onClick={() => setQuitar(null)} aria-label="Cerrar">&times;</button>
            </div>
            {error && <div className={styles.error}>{error}</div>}
            <div className={styles.modalBody}>
              <p style={{ margin: 0 }}>
                El {fechaLarga(quitar.fecha)}/{quitar.fecha.slice(0, 4)} pasará a contar como día de trabajo
                para los tickets que entren a partir de ahora. Los abiertos conservan su plazo.
              </p>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={() => setQuitar(null)}>Cancelar</button>
              <button className={styles.dangerBtn} onClick={confirmQuitar} disabled={saving}>
                {saving ? 'Quitando…' : 'Quitar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
